import type { Rect } from '../../../engine/hex/types';
import { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, TabItem, TabScrollState, UiItem } from './Types';

export class TabBarItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private tabRects: Rect[] = [];
    private tabWidths: number[] = [];
    private rowCount = 1;
    constructor(
        private tabs: TabItem[],
        private scrollState?: TabScrollState,
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        ctx.font = theme.fonts.button;
        this.tabWidths = this.tabs.map((tab) =>
            Math.max(
                theme.button.minWidth,
                ctx.measureText(tab.label).width + theme.tabs.paddingX * 2,
            ),
        );
        let rows = 1;
        let currentWidth = 0;
        this.tabWidths.forEach((w, index) => {
            const needed = index === 0 ? w : w + theme.tabs.gap;
            if (currentWidth > 0 && currentWidth + needed > maxW) {
                rows += 1;
                currentWidth = w;
            } else {
                currentWidth += needed;
            }
        });
        this.rowCount = Math.max(1, rows);
        if (this.scrollState) {
            this.scrollState.max = 0;
            this.scrollState.offset = 0;
        }
        const height =
            this.rowCount * theme.tabs.height + theme.tabs.gap * Math.max(0, this.rowCount - 1);
        return { w: maxW, h: height };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        const height =
            this.rowCount * theme.tabs.height + theme.tabs.gap * Math.max(0, this.rowCount - 1);
        this.rect = { x, y, w, h: height };
        if (this.scrollState) {
            this.scrollState.offset = 0;
            this.scrollState.max = 0;
            this.scrollState.rect = { ...this.rect };
        }
        this.tabRects = [];
        let cx = x;
        let cy = y;
        this.tabs.forEach((tab, index) => {
            const tabW = Math.min(w, this.tabWidths[index] || theme.button.minWidth);
            if (cx > x && cx + tabW > x + w) {
                cx = x;
                cy += theme.tabs.height + theme.tabs.gap;
            }
            this.tabRects.push({ x: cx, y: cy, w: tabW, h: theme.tabs.height });
            cx += tabW + theme.tabs.gap;
        });
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.font = theme.fonts.button;
        ctx.textBaseline = 'top';
        ctx.save();
        ctx.beginPath();
        ctx.rect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
        ctx.clip();
        this.tabs.forEach((tab, index) => {
            const rect = this.tabRects[index];
            if (!rect) return;
            ctx.fillStyle = tab.active ? theme.colors.tabActiveFill : theme.colors.tabInactiveFill;
            ctx.strokeStyle = theme.colors.tabStroke;
            ctx.beginPath();
            ctx.rect(rect.x, rect.y, rect.w, rect.h);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = tab.active ? theme.colors.tabActiveText : theme.colors.tabInactiveText;
            const textW = ctx.measureText(tab.label).width;
            ctx.fillText(tab.label, rect.x + (rect.w - textW) / 2, rect.y + theme.tabs.textOffsetY);
        });
        ctx.restore();
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.tabRects.forEach((rect, index) => {
            const tab = this.tabs[index];
            if (!tab) return;
            out.push({
                id: tab.id,
                enabled: true,
                rect: { x: rect.x, y: rect.y - scrollY, w: rect.w, h: rect.h },
            });
        });
    }
}
